import { useState } from 'react';
import {
  CardHeader,
  Container,
  Card,
  Grid,
  Button,
  CardActions,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { StringMap, TOptions } from 'i18next';
import { useNavigate } from 'react-router-dom';
import Footer from 'pages/Footer';

export type i18translateType = {
  t: (
    key: string | TemplateStringsArray | (string | TemplateStringsArray)[],
    options?: string | TOptions<StringMap> | undefined,
  ) => string;
};

export interface MenuItemType {
  name: string;
  content: string;
  button: string;
  path: string;
}

export default function Dashboard() {
  const { t }: i18translateType = useTranslation();
  const menuItems = t('menu', { returnObjects: true, ns: ['home'] });
  const navigate = useNavigate();

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MenuItemType | null>(null);

  const handleAccessClick = (item: MenuItemType) => {
    if (!item.path || item.path === '') {
      // Show dialog if no path is available
      setSelectedItem(item);
      setOpenDialog(true);
    } else if (item.path.startsWith('http')) {
      // Open external link
      window.open(item.path, '_blank');
    } else {
      // Internal navigation
      navigate(item.path);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
  };

  return (
    <Container maxWidth='xl'>
      <Grid container direction='column' justifyContent='space-between' alignItems='center'>
        <Grid item>
          <Grid container spacing={4}>
            {(Array.isArray(menuItems) ? (menuItems as unknown as MenuItemType[]) : []).map(
              (element: MenuItemType) => (
                <Grid item key={element.name} xs={12} sm={6} md={4}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '12px',
                    boxShadow: 4,
                  }}
                >
                  <CardHeader
                    title={
                      <Grid
                        container
                        direction='row'
                        justifyContent='flex-start'
                        alignItems='flex-start'
                      >
                        <Grid item>
                          <Typography gutterBottom variant='h6' sx={{ fontWeight: 'bold' }}>
                            {element.name}
                          </Typography>
                        </Grid>
                      </Grid>
                    }
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Grid
                      container
                      direction='row'
                      justifyContent='flex-start'
                      alignItems='flex-start'
                    >
                      <Grid item>
                        <Typography variant='body1'>{element.content}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <CardActions>
                    <Grid container direction='row' justifyContent='flex-end' alignItems='flex-end'>
                      <Grid item>
                        <Button
                          size='small'
                          variant='contained'
                          sx={{ textTransform: 'none' }}
                          onClick={() => handleAccessClick(element)}
                        >
                          {element.button}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Access Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          {selectedItem?.name}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedItem?.content}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary', fontStyle: 'italic' }}>
            This module is currently unavailable.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleCloseDialog}
            variant="contained"
            color="primary"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </Container>
  );
}